/**
 * Asks the driver to confirm a dose handed over before its required gap.
 *
 * Four screens funnel into the same delivery helper, so the prompt is raised
 * through a single subscriber rather than wired into each of them: the gate
 * component is mounted once at the app root and answers every request.
 *
 * With no gate mounted the prompt falls back to the browser's own dialog, so
 * a refusal is always seen and an early delivery is never waved through unasked.
 */

let subscriber = null;

export const subscribeDoseGapPrompt = (handler) => {
    subscriber = typeof handler === 'function' ? handler : null;

    return () => {
        if (subscriber === handler) subscriber = null;
    };
};

/**
 * `shown` says whether the driver was actually asked. A caller that declines
 * without being asked has to say something itself, or the delivery stops dead
 * with nothing on screen - which reads as a broken button.
 *
 * @returns {Promise<{confirmed: boolean, reason: string, shown: boolean}>}
 */
/** The prompt as plain text, for the fallback below. */
const promptAsText = (details) => [
    details?.title,
    details?.message,
    ...(Array.isArray(details?.details)
        ? details.details.map((row) => (
            typeof row === 'string' ? row : `${row?.label}: ${row?.value}`
        ))
        : []),
].filter(Boolean).join('\n');

export const requestDoseGapOverride = (details) => {
    if (!subscriber) {
        // Nothing is mounted to ask, so fall back to the browser's own dialog
        // rather than refusing in silence. It is plain, but it needs no
        // styling, no mounting and no bundle to be seen - and a driver refused
        // at a patient's door has to be told something.
        const text = promptAsText(details);

        if (details?.can_override === false) {
            window.alert(text);
            return Promise.resolve({ confirmed: false, reason: '', shown: true });
        }

        return Promise.resolve({
            confirmed: window.confirm(text),
            reason: '',
            shown: true,
        });
    }

    return new Promise((resolve) => {
        let settled = false;
        const settle = (result) => {
            if (settled) return;
            settled = true;
            resolve({ confirmed: false, reason: '', ...(result || {}), shown: true });
        };

        try {
            subscriber(details, settle);
        } catch (error) {
            resolve({ confirmed: false, reason: '', shown: false });
        }
    });
};
